import { trackConfig, type IFramework } from '@/framework';
import { LayerType, TextWritingType, type ILayerProps, type ITextLayerProps } from '@/version-types';
import { applySnapshotSize, FeatureChecker, pushHistory, updateGroupLayer, updateLayer } from '../utils';
import { getTextLayerHost } from '../utils/host';

export interface IWritingMode {
    /**
     * 设置文字方向。
     *
     * @param layerID - 图层 ID。
     * @param writingMode - 文字方向。
     * @returns 更新后的图层或 void。
     */
    setWritingMode(layerID: string, writingMode: TextWritingType): Promise<ILayerProps | void>;
}

const FUNC_ID = trackConfig[LayerType.TEXT_LAYER].writingMode;

const canSwitchToVertical = (layer: ILayerProps): boolean => {
    if (layer.layerType !== LayerType.TEXT_LAYER) return false;
    return (
        !FeatureChecker.usingShapeText(layer) &&
        !FeatureChecker.usingArtText(layer) &&
        !FeatureChecker.usingHighlight(layer)
    );
};

export class WritingMode implements IWritingMode {
    constructor(private framework: IFramework) {}

    /**
     * 使用真实 Text Layer 宿主为书写方向候选重新排版。
     *
     * @param prevLayer - 修改前的 Text Layer。
     * @param writingMode - 目标书写方向。
     * @returns 可提交的图层与 snapshot；候选不支持或宿主未就绪时返回 null。
     */
    private prepare(prevLayer: ILayerProps, writingMode: TextWritingType) {
        if (prevLayer.layerType !== LayerType.TEXT_LAYER) return null;

        const prevLayerData = prevLayer.data as ITextLayerProps;
        if (
            (writingMode === TextWritingType.VERTICAL_RL && !canSwitchToVertical(prevLayer)) ||
            prevLayerData.writingMode === writingMode
        ) {
            return null;
        }

        const nextLayerData: ITextLayerProps = { ...prevLayerData, writingMode };
        let nextLayer: ILayerProps = { ...prevLayer, data: nextLayerData };

        if (prevLayer.__fixedWidth !== false) {
            const previousInlineSize =
                prevLayerData.writingMode === TextWritingType.HORIZONTAL_TB ? prevLayer.width : prevLayer.height;
            nextLayer =
                writingMode === TextWritingType.HORIZONTAL_TB
                    ? { ...nextLayer, width: previousInlineSize }
                    : { ...nextLayer, height: previousInlineSize };
        }

        const resolution = getTextLayerHost(this.framework.service.edgelessCanvas, nextLayer.id)?.layout(nextLayer);
        // 书写模式与尺寸必须来自同一份 ready 候选排版。
        if (resolution?.status !== 'ready') return null;
        const snapshot = resolution.value;

        nextLayer = applySnapshotSize(nextLayer, snapshot);
        return {
            layer: {
                ...nextLayer,
                left: prevLayer.left + (prevLayer.width - nextLayer.width) / 2,
                top: prevLayer.top + (prevLayer.height - nextLayer.height) / 2,
            },
            snapshot,
        };
    }

    private setGroup(prevLayer: ILayerProps, writingMode: TextWritingType) {
        const children = prevLayer.children;
        if (children.length === 0 || !children.every((child) => child.layerType === LayerType.TEXT_LAYER)) return;
        if (children.every((child) => (child.data as ITextLayerProps).writingMode === writingMode)) return prevLayer;

        const layers: ILayerProps[] = [];
        for (const child of children) {
            if ((child.data as ITextLayerProps).writingMode === writingMode) {
                layers.push(child);
                continue;
            }

            const prepared = this.prepare(child, writingMode);
            if (!prepared) return;
            layers.push(prepared.layer);
        }

        return updateGroupLayer(this.framework, prevLayer, layers, {
            funcID: FUNC_ID,
            update: true,
            writeHistory: true,
        });
    }

    private setSingle(prevLayer: ILayerProps, writingMode: TextWritingType) {
        if (prevLayer.layerType !== LayerType.TEXT_LAYER) return;
        if ((prevLayer.data as ITextLayerProps).writingMode === writingMode) return prevLayer;

        const prepared = this.prepare(prevLayer, writingMode);
        if (!prepared) return;

        updateLayer(this.framework, prepared.layer, { activate: true });
        pushHistory(this.framework, prevLayer, prepared.layer, FUNC_ID);
        return prepared.layer;
    }

    async setWritingMode(layerID: string, writingMode: TextWritingType) {
        const prevLayer = this.framework.service.flatLayer.getLayer(layerID, true);
        if (!prevLayer) return;

        return prevLayer.layerType === LayerType.GROUP_LAYER
            ? this.setGroup(prevLayer, writingMode)
            : this.setSingle(prevLayer, writingMode);
    }
}
